import { motion } from "framer-motion";
import { Linkedin, ArrowUpRight } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FOOTER } from "@/constants/testIds";
import { useBookConsultation } from "@/hooks/useBookConsultation";
import { LEGAL_NAV } from "@/data/legal";

const slugify = (s) => s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const linkCls =
  "font-serif text-sm text-canvas/75 hover:text-canvas transition-colors duration-300";

export const Footer = () => {
  const bookConsultation = useBookConsultation();
  const navigate = useNavigate();
  const location = useLocation();

  const goPractice = (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      document.getElementById("practice-areas")?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => document.getElementById("practice-areas")?.scrollIntoView({ behavior: "smooth" }), 400);
    }
  };

  return (
    <footer id="footer-section" data-testid={FOOTER.section}>
      {/* CTA band — forest green */}
      <div className="bg-forest text-canvas">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-8 md:pt-10 pb-5 md:pb-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif text-[14px] md:text-[22px] leading-tight max-w-2xl"
            >
              Let&apos;s make your next<br />business move matter.
            </motion.h2>
            <button
              data-testid={FOOTER.bookCta}
              onClick={bookConsultation}
              className="group shrink-0 inline-flex items-center gap-2.5 font-sans text-[11px] tracking-[0.14em] uppercase font-bold px-6 py-3 bg-canvas text-charcoal border border-canvas transition-colors duration-300 hover:bg-gold hover:border-gold"
            >
              Book a Consultation
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      {/* Lower footer — charcoal */}
      <div className="bg-charcoal text-canvas">
        <div className="max-w-[1120px] mx-auto px-6 lg:px-12 pt-4 md:pt-5 pb-5 md:pb-6">
          <div className="grid grid-cols-2 md:grid-cols-12 gap-x-8 gap-y-6">
            {/* Brand */}
            <div className="col-span-2 md:col-span-3">
              <img
                src="/amadi-logo.svg"
                alt="AMADI"
                data-testid={FOOTER.firmName}
                className="h-7 w-auto"
              />
              <p className="mt-2.5 font-sans text-[11px] tracking-[0.22em] uppercase text-canvas/70">
                Legal Consultancy
              </p>
            </div>

            {/* Explore */}
            <div className="md:col-span-3" data-testid="footer-quick-links">
              <p className="font-sans text-[9px] tracking-[0.2em] uppercase text-gold mb-3.5">
                Explore
              </p>
              <div className="flex flex-col gap-1.5">
                <Link data-testid="footer-link-home" to="/" className={linkCls}>Home</Link>
                <a data-testid="footer-link-practice-areas" href="/" onClick={goPractice} className={linkCls}>Practice Areas</a>
                <Link data-testid="footer-link-about" to="/about" className={linkCls}>About</Link>
                <Link data-testid="footer-link-insights" to="/insights" className={linkCls}>Insights</Link>
                <Link data-testid="footer-link-contact" to="/contact" className={linkCls}>Contact</Link>
                <a
                  data-testid="footer-link-haq-hr"
                  href="https://hr.amadilegal.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group inline-flex items-center gap-1.5 ${linkCls}`}
                >
                  Haq HR
                  <ArrowUpRight className="w-3 h-3 text-gold/70 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
                </a>
              </div>
            </div>

            {/* Legal */}
            <div className="md:col-span-3" data-testid="footer-legal-links">
              <p className="font-sans text-[9px] tracking-[0.2em] uppercase text-gold mb-3.5">
                Legal
              </p>
              <div className="flex flex-col gap-1.5">
                {LEGAL_NAV.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`footer-legal-${slugify(l.label)}`}
                    className={linkCls}
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Connect */}
            <div className="md:col-span-3">
              <p className="font-sans text-[9px] tracking-[0.2em] uppercase text-gold mb-3.5">
                Connect
              </p>
              <a
                data-testid={FOOTER.email}
                href="mailto:connect@amadi.io"
                className="block font-serif text-base text-canvas/75 hover:text-canvas transition-colors duration-300"
              >
                connect@amadi.io
              </a>
              <a
                data-testid={FOOTER.linkedin}
                href="https://www.linkedin.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="mt-6 inline-flex items-center justify-center w-11 h-11 border border-canvas/25 text-canvas/75 hover:border-gold hover:text-gold transition-colors duration-300"
              >
                <Linkedin className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-6 pt-4 border-t border-canvas/15 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <p className="font-sans text-[11px] tracking-[0.14em] uppercase text-canvas/65">
              © {new Date().getFullYear()} AMADI. All rights reserved.
            </p>
            <p data-testid="footer-group-credit" className="font-sans text-[11px] text-canvas/60 leading-relaxed">
              AMADI Legal is the legal practice of{" "}
              <a
                data-testid="footer-amadi-group-link"
                href="https://amadi.io"
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2 hover:text-gold transition-colors duration-300"
              >
                Amadi Group
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
